import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import { Trash2, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import FieldError from '../FieldError';
import { buildDocTypes, isImageFile, isPdfFile } from '../intakeConstants';

/** Step 7 — File upload (with preview + nav) + document-type checkboxes. */
export default function DocumentsStep({
  formData, errors,
  previewIdx, setPreviewIdx,
  onFileUpload, onDrop, onDragOver, onRemoveFile,
  onToggleDocument,
}) {
  const docTypes = buildDocTypes(formData.otherVehicle);
  return (
    <div>
      <label className="text-blue-500 text-2xl font-bold mb-8 block">Document Uploads</label>

      <div className="grid grid-cols-1 md:grid-cols-[1fr,1fr] gap-6 md:gap-8">
        {/* Left: upload area */}
        <div>
          <div className="w-full">
            {formData.uploadedFiles.length > 0 && (
              <div className="border border-gray-300 rounded-lg p-4 bg-white mb-4" data-testid="intake-file-preview">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-blue-500 underline text-sm truncate flex-1">
                    {formData.uploadedFiles[previewIdx]?.name}
                  </span>
                  <span className="text-gray-400 text-xs whitespace-nowrap">
                    {formData.uploadedFiles[previewIdx]
                      ? (formData.uploadedFiles[previewIdx].size / 1024).toFixed(1) + ' KB'
                      : ''}
                  </span>
                  <button
                    onClick={() => onRemoveFile(previewIdx)}
                    className="text-gray-400 hover:text-red-500"
                    data-testid="intake-delete-file"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded flex items-center justify-center min-h-[180px] relative">
                  {formData.uploadedFiles.length > 1 && previewIdx > 0 && (
                    <button
                      onClick={() => setPreviewIdx((i) => Math.max(0, i - 1))}
                      className="absolute left-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 z-10"
                      data-testid="intake-prev-file"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                  )}

                  {formData.uploadedFiles[previewIdx] && isImageFile(formData.uploadedFiles[previewIdx]) ? (
                    <img
                      src={URL.createObjectURL(formData.uploadedFiles[previewIdx])}
                      alt={formData.uploadedFiles[previewIdx].name}
                      className="max-h-[180px] max-w-full object-contain"
                    />
                  ) : formData.uploadedFiles[previewIdx] && isPdfFile(formData.uploadedFiles[previewIdx]) ? (
                    <iframe
                      src={URL.createObjectURL(formData.uploadedFiles[previewIdx])}
                      title={formData.uploadedFiles[previewIdx].name}
                      className="w-full h-[180px] border-0"
                    />
                  ) : (
                    <div className="text-gray-400 text-sm text-center p-4">
                      <FileText className="w-10 h-10 mx-auto mb-1 text-gray-300" />
                      <p className="font-medium text-gray-500">
                        {formData.uploadedFiles[previewIdx]?.name.split('.').pop().toUpperCase()}
                      </p>
                      <p>Preview not supported</p>
                    </div>
                  )}

                  {formData.uploadedFiles.length > 1 && previewIdx < formData.uploadedFiles.length - 1 && (
                    <button
                      onClick={() => setPreviewIdx((i) => Math.min(formData.uploadedFiles.length - 1, i + 1))}
                      className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 z-10"
                      data-testid="intake-next-file"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  )}
                </div>

                <p className="text-gray-500 text-xs mt-2">
                  File {previewIdx + 1} of {formData.uploadedFiles.length}
                </p>
              </div>
            )}

            <div
              className={`border-[3px] border-dashed rounded-lg p-8 flex flex-col items-center justify-center bg-white w-full ${
                errors.uploadedFiles ? 'border-red-500' : 'border-gray-300'
              }`}
              style={{ minHeight: formData.uploadedFiles.length > 0 ? '160px' : '420px' }}
              onDrop={onDrop}
              onDragOver={onDragOver}
            >
              <div className="flex gap-6 mb-4 text-gray-300">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <path d="M12 18v-6m-3 3l3 3 3-3" strokeWidth="2" />
                </svg>
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <p className="text-lg text-gray-500 mb-1 font-light text-center">Drag and drop your file</p>
              <input
                type="file"
                id="fileUpload"
                multiple
                onChange={onFileUpload}
                className="hidden"
              />
              <label htmlFor="fileUpload" className="text-blue-500 text-base underline cursor-pointer hover:text-blue-600">
                Click to select a file
              </label>
              <p className="text-gray-500 text-base font-light text-center">or drop it here</p>
            </div>
            <FieldError message={errors.uploadedFiles} />
          </div>
        </div>

        {/* Right: document type checkboxes */}
        <div className="flex flex-col">
          <label className="text-blue-500 text-lg font-semibold mb-4 block">
            Select Uploaded Pictures/Documents
          </label>
          <div className="space-y-7">
            {docTypes.map((doc) => (
              <div key={doc} className="flex items-center space-x-2" style={{ marginBottom: '16px' }}>
                <Checkbox
                  id={doc}
                  checked={formData.selectedDocuments.includes(doc)}
                  onCheckedChange={() => onToggleDocument(doc)}
                  className="w-5 h-5 border-2 flex-shrink-0"
                />
                <Label htmlFor={doc} className="text-sm text-gray-600 cursor-pointer whitespace-nowrap">
                  {doc}
                </Label>
              </div>
            ))}
          </div>
          <FieldError message={errors.selectedDocuments} className="mt-4" />
        </div>
      </div>
    </div>
  );
}
